<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="750px">
      <v-skeleton-loader v-if="loading" type="article, actions"></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title style="padding: 10px 0 0 10px; height: 20px;">
          <v-col cols="12">
            <span style="font-size: 20px;">{{ $t('wkt.admin.calendar.copy-exercise-window.title') }}</span>
          </v-col>
        </v-card-title>
        <v-card-text style="padding-bottom: 0;">
          <v-container @wheel="onWheel">
            <v-form ref="form">
              <v-row no-gutters class="mt-8 d-flex align-center">
                <v-col cols="8" class="p-0">
                  <v-autocomplete
                    attach
                    v-model="data.srcCalendarId"
                    :items="srcCalendars"
                    item-text="name"
                    item-value="id"
                    menu-props="bottom, offsetY"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.source_calendar')"
                    :rules="[validationRules.required]"
                    class="mr-2"
                    @change="updateDstCalendars()"
                  />
                </v-col>
                <v-col cols="4" class="p-0">
                  <v-chip
                    small
                    v-if="data.srcCalendarId"
                    :color="srcCalendarModeBkColor"
                    style="color: #00000099"
                  >{{ srcCalendarModeText }}</v-chip>
                </v-col>
              </v-row>
              <v-row no-gutters class="d-flex align-center">
                <v-col cols="8" class="p-0">
                  <v-autocomplete
                    attach
                    v-model="data.dstCalendarId"
                    :items="dstCalendars"
                    item-text="name"
                    item-value="id"
                    menu-props="bottom, offsetY"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.destination_calendar')"
                    :rules="[validationRules.required]"
                    class="mr-2"
                    @change="updateSrcCalendars()"
                  />
                </v-col>
                <v-col cols="4" class="p-0">
                  <v-chip
                    small
                    v-if="data.dstCalendarId"
                    :color="dstCalendarModeBkColor"
                    style="color: #00000099"
                  >{{ dstCalendarModeText }}</v-chip>
                </v-col>
              </v-row>
              <v-row no-gutters class="d-flex align-center">
                <v-col cols="4">
                  <v-select
                    attach
                    v-model="data.exercise"
                    :items="exercises"
                    item-text="name"
                    item-value="id"
                    menu-props="bottom, offsetY"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.exercise')"
                    :rules="[validationRules.required]"
                    :disabled="disableExercise"
                    @change="updateDisableOkButton()"
                  />
                </v-col>
              </v-row>
              <v-row class="my-2 mx-0">
                <h3>{{ $t('wkt.admin.calendar.copy-exercise-window.select_info_to_copy') }}</h3>
                <v-col cols="12" class="mt-4 pa-0">
                  <v-checkbox
                    v-model="data.copyDescription"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.copy_description')"
                    class="mt-0"
                    @change="updateDisableOkButton()"
                  />
                </v-col>
                <v-col cols="12" class="pa-0">
                  <!--<v-checkbox
											v-model="data.copyDayType"
											:label="$t('wkt.admin.calendar.copy-exercise-window.copy_dayType')"
											class="mt-0"
											@change="updateDisableOkButton()"
                  />-->
                  <span
                    class="mb-4 d-block text-subtitle-1"
                  >{{ $t('wkt.admin.calendar.copy-exercise-window.copy_dayType') }}</span>
                  <v-radio-group
                    v-model="data.copyDayType"
                    row
                    class="mt-0 ml-4"
                    @change="updateDisableOkButton()"
                  >
                    <v-radio
                      v-for="(option, index) in copyDayTypeOptions"
                      :key="index"
                      :label="option.label"
                      :value="option.value"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="12" class="pa-0">
                  <v-checkbox
                    v-model="data.copyCheckinAllowed"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.copy_checkinAllowed')"
                    class="mt-0"
                    @change="updateDisableOkButton()"
                  />
                </v-col>
                <v-col cols="12" class="pa-0">
                  <v-checkbox
                    ref="copyWorkHours"
                    v-model="data.copyWorkHours"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.copy_workHours')"
                    :disabled="disableCopyWorkHours"
                    class="mt-0"
                    @change="updateDisableOkButton()"
                  />
                </v-col>
                <v-col cols="12" class="pa-0">
                  <v-checkbox
                    v-model="data.copyPredefinedActivities"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.copy_predefinedActivities')"
                    :disabled="disableCopyPredefinedActivities"
                    class="mt-0"
                    @change="updateDisableOkButton()"
                  />
                </v-col>
                <v-col cols="12" class="pa-0">
                  <v-checkbox
                    v-model="data.copyApplyTimeConstraint"
                    :label="$t('wkt.admin.calendar.copy-exercise-window.copy_applyTimeConstraint')"
                    class="mt-0"
                    @change="updateDisableOkButton()"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :disabled="disableOkButton"
            color="#185EA9"
            text
            @click="onOk()"
            style="font-size: 0.9em; font-weight: 400;"
          >{{ $t('wkt.admin.common.dialog-buttons.ok').toUpperCase() }}</v-btn>
          <v-btn
            text
            @click="showDialog=false"
            style="font-size: 0.9em; font-weight: 400;"
          >{{ $t('wkt.admin.common.dialog-buttons.cancel').toUpperCase() }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import api from "../../../api/worktime";

export default {
  name: "CopyCalendarExerciseDialog",
  mixins: [BaseModule],
  components: {},
  data() {
    return {
      showDialog: false,
      loading: true,
      data: {},
      srcCalendars: [],
      dstCalendars: [],
      initialCalendars: [],
      exercises: [],
      disableExercise: true,
      disableCopyWorkHours: true,
      disableCopyPredefinedActivities: true,
      disableOkButton: true,
      srcCalendarModeBkColor: "",
      srcCalendarModeText: "",
      dstCalendarModeBkColor: "",
      dstCalendarModeText: "",
      copyDayTypeOptions: [
        {
          label: this.$t(
            "wkt.admin.calendar.copy-exercise-window.copy_dayType_all"
          ),
          value: 2,
        },
        {
          label: this.$t(
            "wkt.admin.calendar.copy-exercise-window.copy_dayType_holidays"
          ),
          value: 1,
        },
        {
          label: this.$t(
            "wkt.admin.calendar.copy-exercise-window.copy_dayType_none"
          ),
          value: 0,
        },
      ],
    };
  },
  computed: {},
  methods: {
    onWheel(event) {
      event.stopPropagation();
    },
    // eslint-disable-next-line no-unused-vars
    open: function (action, payload) {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      if (!payload) {
        this.srcCalendars = [];
        this.dstCalendars = [];
        this.initialCalendars = [];
        this.srcCalendarModeBkColor = "";
        this.srcCalendarModeText = "";
        this.dstCalendarModeBkColor = "";
        this.dstCalendarModeText = "";
        this.disableExercise = true;
        this.disableCopyWorkHours = true;
        this.disableCopyPredefinedActivities = true;
        this.disableOkButton = true;
        this.data.srcCalendarId = null;
        this.data.dstCalendarId = null;
        this.data.copyDayType = 0;
        this.data.copyDescription = false;
        this.data.copyCheckinAllowed = false;
        this.data.copyWorkHours = false;
        this.data.copyPredefinedActivities = false;
        this.data.copyApplyTimeConstraint = false;
      }

      this.showDialog = true;

      this.loading = true;
      api.getCalendars(
        (res) => {
          this.initialCalendars = res;
          this.srcCalendars = [...this.initialCalendars];
          this.dstCalendars = [...this.initialCalendars];
          this.loading = false;
        },
        (error) => {
          this.$emit("show-alert", {
            text: error,
            color: "error",
            timeout: 3000,
          });
          this.loading = false;
        }
      );
    },
    updateSrcCalendars() {
      this.srcCalendars = [...this.initialCalendars];

      if (this.data.dstCalendarId) {
        this.setDstCalendarModeText();
        this.setDstCalendarModeBkColor();

        this.srcCalendars = this.srcCalendars.filter(
          (x) => x.id !== this.data.dstCalendarId
        );
      }

      this.updateExercises();
    },
    updateDstCalendars() {
      this.dstCalendars = [...this.initialCalendars];

      if (this.data.srcCalendarId) {
        this.setSrcCalendarModeText();
        this.setSrcCalendarModeBkColor();

        this.dstCalendars = this.dstCalendars.filter(
          (x) => x.id !== this.data.srcCalendarId
        );
      }

      this.updateExercises();
    },
    updateExercises() {
      if (!this.data.srcCalendarId || !this.data.dstCalendarId) {
        return;
      }

      this.disableExercise = false;

      const srcCalendar = this.initialCalendars.find(
        (c) => c.id === this.data.srcCalendarId
      );
      const dstCalendar = this.initialCalendars.find(
        (c) => c.id === this.data.dstCalendarId
      );

      if (srcCalendar.calendarMode !== dstCalendar.calendarMode) {
        this.disableCopyWorkHours = true;
        this.disableCopyPredefinedActivities = true;
      }
      if (srcCalendar.calendarMode === 0 && dstCalendar.calendarMode === 0) {
        this.disableCopyWorkHours = false;
        this.disableCopyPredefinedActivities = true;
      }
      if (srcCalendar.calendarMode === 1 && dstCalendar.calendarMode === 1) {
        this.disableCopyWorkHours = true;
        this.disableCopyPredefinedActivities = false;
      }

      this.exercises = [];

      const srcExerciseMin = srcCalendar.exerciseMin;
      const srcExerciseMax = srcCalendar.exerciseMax;

      const dstExerciseMin = dstCalendar.exerciseMin;
      const dstExerciseMax = dstCalendar.exerciseMax;

      for (
        let exercise = srcExerciseMin;
        exercise <= srcExerciseMax;
        exercise++
      ) {
        if (exercise < dstExerciseMin || exercise > dstExerciseMax) {
          continue;
        }

        this.exercises.push({ id: exercise, name: exercise });
      }

      this.updateDisableOkButton();
    },
    setSrcCalendarModeText() {
      if (!this.data.srcCalendarId) {
        return "";
      }

      const cal = this.initialCalendars.find(
        (c) => c.id === this.data.srcCalendarId
      );

      if (!cal) {
        return "";
      }

      if (cal.calendarMode === 0) {
        this.srcCalendarModeText = this.$t(
          "wkt.admin.common.calendar.standard_checkin"
        );
      } else if (cal.calendarMode === 1) {
        this.srcCalendarModeText = this.$t(
          "wkt.admin.common.calendar.assisted_checkin"
        );
      } else {
        this.srcCalendarModeText = "";
      }
    },
    setDstCalendarModeText() {
      if (!this.data.dstCalendarId) {
        return "";
      }

      const cal = this.initialCalendars.find(
        (c) => c.id === this.data.dstCalendarId
      );

      if (!cal) {
        return "";
      }

      if (cal.calendarMode === 0) {
        this.dstCalendarModeText = this.$t(
          "wkt.admin.common.calendar.standard_checkin"
        );
      } else if (cal.calendarMode === 1) {
        this.dstCalendarModeText = this.$t(
          "wkt.admin.common.calendar.assisted_checkin"
        );
      } else {
        this.dstCalendarModeText = "";
      }
    },
    setSrcCalendarModeBkColor() {
      if (!this.data.srcCalendarId) {
        return "";
      }

      const cal = this.initialCalendars.find(
        (c) => c.id === this.data.srcCalendarId
      );

      if (!cal) {
        return "";
      }

      if (cal.calendarMode === 0) {
        this.srcCalendarModeBkColor = "amber lighten-4";
      } else if (cal.calendarMode === 1) {
        this.srcCalendarModeBkColor = "red lighten-5";
      } else {
        this.srcCalendarModeBkColor = "";
      }
    },
    setDstCalendarModeBkColor() {
      if (!this.data.dstCalendarId) {
        return "";
      }

      const cal = this.initialCalendars.find(
        (c) => c.id === this.data.dstCalendarId
      );

      if (!cal) {
        return "";
      }

      if (cal.calendarMode === 0) {
        this.dstCalendarModeBkColor = "amber lighten-4";
      } else if (cal.calendarMode === 1) {
        this.dstCalendarModeBkColor = "red lighten-5";
      } else {
        this.dstCalendarModeBkColor = "";
      }
    },
    hasAnyOptionSelected() {
      return (
        this.data.copyDescription ||
        this.data.copyDayType !== 0 ||
        this.data.copyCheckinAllowed ||
        (this.data.copyWorkHours && !this.disableCopyWorkHours) ||
        (this.data.copyPredefinedActivities &&
          !this.disableCopyPredefinedActivities) ||
        this.data.copyApplyTimeConstraint
      );
    },
    updateDisableOkButton() {
      this.disableOkButton =
        !this.data.srcCalendarId ||
        !this.data.dstCalendarId ||
        !this.data.exercise ||
        !this.hasAnyOptionSelected();
    },
    onOk() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.data.copyWorkHours =
        this.data.copyWorkHours && !this.disableCopyWorkHours;
      this.data.copyPredefinedActivities =
        this.data.copyPredefinedActivities && !this.copyPredefinedActivities;

      this.loading = true;
      api.copyCalendarExercise(
        this.data,
        (res) => {
          this.loading = false;
          if (res.rsOK) {
            this.showDialog = false;
            this.$emit("show-alert", {
              text: res.message,
              color: "success",
              timeout: 3000,
            });
          } else {
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error,
            color: "error",
            timeout: 3000,
          });
        }
      );
    },
  },
};
</script>